import { randomInt } from 'crypto';
import { ConversationCode, InterviewCodeType, Report } from '@/types/pulsedb';
import {
  ConversationStatus,
  SectionedReport,
  SectionedReportSection
} from '@/types/pulse';
import { parseSectionedReport } from '@/utils/reports/parse';
import type { Json } from '@/types/db';
import { SupabaseClient } from '@supabase/supabase-js';

export interface ReportHeaderContext {
  title: string;
  generated_at: string;
  conversation_count: number;
}

export const getLatestReportForInterview = async (
  supabase: SupabaseClient,
  interviewId: number
): Promise<Report | null> => {
  const { data } = await supabase
    .from('reports')
    .select('*')
    .eq('interview_id', interviewId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
    .throwOnError();
  return data;
};

/** The most recent report for an interview, by descending id. */
export const getRecentReport = async (
  supabase: SupabaseClient,
  interviewId: number
): Promise<Report | null> => {
  const { data } = await supabase
    .from('reports')
    .select('*')
    .eq('interview_id', interviewId)
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle()
    .throwOnError();
  return data;
};

/** Random 13-char [0-9a-z] slug. */
export function generateReportSlug(length = 13): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  let slug = '';
  // randomInt is unbiased (rejection sampling), unlike randomBytes + modulo.
  for (let i = 0; i < length; i++) slug += chars[randomInt(chars.length)];
  return slug;
}

/**
 * Write a report for an interview: update the most recent row in place if one
 * exists (bumping `generated_at`), else create a fresh one. The single row write
 * that makes the report visible — it runs last in the workflow so a failed run
 * leaves no report behind.
 */
export const upsertReport = async (
  supabase: SupabaseClient,
  interviewId: number,
  data: SectionedReport
): Promise<Report> => {
  const existing = await getRecentReport(supabase, interviewId);
  if (existing) {
    const { data: updated } = await supabase
      .from('reports')
      .update({
        data: data as unknown as Json,
        generated_at: new Date().toISOString()
      })
      .eq('id', existing.id)
      .select('*')
      .single()
      .throwOnError();
    return updated;
  }
  const { data: created } = await supabase
    .from('reports')
    .insert({
      interview_id: interviewId,
      slug: generateReportSlug(),
      data: data as unknown as Json
    })
    .select('*')
    .single()
    .throwOnError();
  return created;
};

/**
 * Raised when a section write loses a race: the report row moved between the
 * read that produced the edit and the write that would land it. Distinct from a
 * failure, since nothing is wrong — the edit is simply stale.
 */
export class ReportSectionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReportSectionConflictError';
  }
}

/**
 * Replace a single section of an interview's most recent report, in place.
 *
 * `expectedGeneratedAt` is the `generated_at` the caller read the section from,
 * and the update is conditional on it still holding — a concurrent section edit
 * or a full regeneration lands its own `generated_at`, so the later of the two
 * writes is rejected rather than silently overwriting the earlier one. The
 * condition rides on the UPDATE itself; the pre-read only buys a clearer error.
 */
export const updateReportSection = async (
  supabase: SupabaseClient,
  interviewId: number,
  slug: string,
  section: SectionedReportSection,
  expectedGeneratedAt: string
): Promise<void> => {
  const existing = await getRecentReport(supabase, interviewId);
  if (!existing) {
    throw new ReportSectionConflictError(
      `No report to edit for interview ${interviewId}`
    );
  }
  if (existing.generated_at !== expectedGeneratedAt) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while section "${slug}" was being edited`
    );
  }

  const parsed = parseSectionedReport(existing.data);
  const index = parsed?.sections.findIndex((s) => s.slug === slug) ?? -1;
  if (!parsed || index === -1) {
    throw new ReportSectionConflictError(
      `Section "${slug}" is no longer in report ${existing.id}`
    );
  }
  parsed.sections[index] = section;

  const { data: updated } = await supabase
    .from('reports')
    .update({
      data: parsed as unknown as Json,
      generated_at: new Date().toISOString()
    })
    .eq('id', existing.id)
    .eq('generated_at', expectedGeneratedAt)
    .select('id')
    .throwOnError();

  if (!updated || updated.length === 0) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while section "${slug}" was being written`
    );
  }
};

/**
 * Set which sections an interview's report has, and in what order.
 *
 * Takes slugs rather than sections on purpose: the stored sections are the ones
 * kept and reordered, so rearranging a report cannot rewrite what it says.
 * Returns the sections that were dropped, whose reels and signals the caller
 * retires.
 *
 * Conditional on `expectedGeneratedAt` for the same reason `updateReportSection`
 * is, and it leaves `generated_at` alone on the way out: this changes what the
 * report contains, not when it was written, and a section revision already in
 * flight reads that value to tell whether the report moved under it.
 */
export const reorderReportSections = async (
  supabase: SupabaseClient,
  interviewId: number,
  slugs: string[],
  expectedGeneratedAt: string
): Promise<{ removed: SectionedReportSection[] }> => {
  const existing = await getRecentReport(supabase, interviewId);
  if (!existing) {
    throw new ReportSectionConflictError(
      `No report to edit for interview ${interviewId}`
    );
  }
  if (existing.generated_at !== expectedGeneratedAt) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while its sections were being rearranged`
    );
  }

  const parsed = parseSectionedReport(existing.data);
  if (!parsed) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} has no sections to rearrange`
    );
  }

  const remaining = new Map(parsed.sections.map((s) => [s.slug, s]));
  const kept: SectionedReportSection[] = [];
  for (const slug of slugs) {
    const section = remaining.get(slug);
    if (!section) {
      throw new ReportSectionConflictError(
        `Section "${slug}" is no longer in report ${existing.id}`
      );
    }
    // Dropped as it is taken, so a slug named twice cannot duplicate a section.
    remaining.delete(slug);
    kept.push(section);
  }
  if (kept.length === 0) {
    throw new Error('A report has to keep at least one section');
  }
  const removed = Array.from(remaining.values());

  const { data: updated } = await supabase
    .from('reports')
    .update({ data: { ...parsed, sections: kept } as unknown as Json })
    .eq('id', existing.id)
    .eq('generated_at', expectedGeneratedAt)
    .select('id')
    .throwOnError();

  if (!updated || updated.length === 0) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while its sections were being written`
    );
  }

  return { removed };
};

/**
 * Replace the executive summary of an interview's most recent report with the
 * author's own text. The sections are stored back as they were.
 *
 * Conditional on `expectedGeneratedAt` and leaves `generated_at` alone, for the
 * same reasons `reorderReportSections` does.
 */
export const updateReportExecutiveSummary = async (
  supabase: SupabaseClient,
  interviewId: number,
  executiveSummary: string,
  expectedGeneratedAt: string
): Promise<void> => {
  const trimmed = executiveSummary.trim();
  if (!trimmed) {
    throw new Error('An executive summary cannot be empty');
  }

  const existing = await getRecentReport(supabase, interviewId);
  if (!existing) {
    throw new ReportSectionConflictError(
      `No report to edit for interview ${interviewId}`
    );
  }
  if (existing.generated_at !== expectedGeneratedAt) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while its executive summary was being edited`
    );
  }

  const parsed = parseSectionedReport(existing.data);
  if (!parsed) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} has no body to edit`
    );
  }

  const { data: updated } = await supabase
    .from('reports')
    .update({
      data: { ...parsed, executive_summary: trimmed } as unknown as Json
    })
    .eq('id', existing.id)
    .eq('generated_at', expectedGeneratedAt)
    .select('id')
    .throwOnError();

  if (!updated || updated.length === 0) {
    throw new ReportSectionConflictError(
      `Report ${existing.id} changed while its executive summary was being written`
    );
  }
};

export const getReportBySlug = async (
  supabase: SupabaseClient,
  slug: string
): Promise<Report | null> => {
  const { data } = await supabase
    .from('reports')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()
    .throwOnError();
  return data;
};

export const getReportHeaderContext = async (
  supabase: SupabaseClient,
  report: Report
): Promise<ReportHeaderContext | null> => {
  const [{ data: interview }, { count }] = await Promise.all([
    supabase
      .from('interviews')
      .select('title, name')
      .eq('id', report.interview_id)
      .single()
      .throwOnError(),
    supabase
      .from('conversations')
      .select('id', { count: 'exact', head: true })
      .eq('interview_id', report.interview_id)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false)
      .throwOnError()
  ]);
  if (!interview) return null;
  return {
    title: interview.title ?? interview.name ?? 'Untitled interview',
    generated_at: report.generated_at,
    conversation_count: count ?? 0
  };
};

export const getCodetypeWithCodes = async (
  supabase: SupabaseClient,
  codetypeId: number
): Promise<{
  codetype: InterviewCodeType;
  codes: ConversationCode[];
} | null> => {
  const [{ data: codetype }, { data: codes }] = await Promise.all([
    supabase
      .from('interview_codetypes')
      .select('*')
      .eq('id', codetypeId)
      .eq('deleted', false)
      .maybeSingle()
      .throwOnError(),
    supabase
      .from('conversation_codes')
      .select('*, conversations!inner(deleted)')
      .eq('codetype_id', codetypeId)
      .eq('conversations.deleted', false)
      .throwOnError()
  ]);
  if (!codetype) return null;
  return { codetype, codes: (codes as ConversationCode[]) ?? [] };
};
